import axios from 'Src/helpers/api/axiosInstance';
import { AppUser } from 'Src/views/App/App';
import { ConfirmationRequestTemplate } from 'Src/models/confirmationRequestTemplate';
import { FormDefinition } from 'Src/types/formSchema';
import { ConfirmationRequestTemplateField } from 'Src/models/confirmationRequestTemplate';
import { FormTemplateTag } from './formTemplateTags';

export type FormTemplateType = 'CONSENT' | 'CLUB_BOOKING' | 'TOURNAMENT_CONFIRMATION';

export type FormTemplate = ConfirmationRequestTemplate & {
  templateType: FormTemplateType;
  tagIds?: string[];
  tags?: FormTemplateTag[];
};

export type FormTemplatePayload = {
  name: string;
  isDefault?: boolean;
  editableByRoles?: string[];
  creatableByRoles?: string[];
  tagIds?: string[];
  schemaVersion: 2;
  formDefinition: FormDefinition;
  fields: ConfirmationRequestTemplateField[];
};

const collectionPath = (type: FormTemplateType): string => {
  switch (type) {
    case 'CONSENT':
      return 'formTemplates/consent';
    case 'CLUB_BOOKING':
      return 'formTemplates/clubBooking';
    case 'TOURNAMENT_CONFIRMATION':
      return 'formTemplates/tournamentConfirmation';
  }
};

const configFor = (user: AppUser) => ({ headers: { usid: user.roleSessionKey } });

export type FormTemplateQuery = {
  search?: string;
  status?: 'ALL' | 'DEFAULT' | 'CUSTOM';
  tagId?: string;
};

export function getFormTemplates(
  user: AppUser,
  type: FormTemplateType,
  query: string | FormTemplateQuery = ''
): Promise<FormTemplate[]> {
  const params = typeof query === 'string'
    ? query ? { search: query } : undefined
    : {
        search: query.search || undefined,
        status: query.status && query.status !== 'ALL' ? query.status : undefined,
        tagId: query.tagId || undefined
      };
  return axios
    .get(`${window.apiBase}/i/schools/${user.activeSchoolId}/${collectionPath(type)}`, {
      ...configFor(user),
      params
    })
    .then(response => response.data.map((item: ConfirmationRequestTemplate) => ({ ...item, templateType: type })));
}

export function getFormTemplatesForTypes(
  user: AppUser,
  types: FormTemplateType[],
  query: string | FormTemplateQuery = ''
): Promise<FormTemplate[]> {
  return Promise.all(types.map(type => getFormTemplates(user, type, query))).then(groups =>
    groups.reduce<FormTemplate[]>((result, items) => result.concat(items), [])
  );
}

export function createFormTemplate(
  user: AppUser,
  type: FormTemplateType,
  data: FormTemplatePayload
): Promise<FormTemplate> {
  return axios
    .post(`${window.apiBase}/i/schools/${user.activeSchoolId}/${collectionPath(type)}`, data, configFor(user))
    .then(response => ({ ...response.data, templateType: type }));
}

export function updateFormTemplate(
  user: AppUser,
  type: FormTemplateType,
  templateId: string,
  data: Partial<FormTemplatePayload>
): Promise<FormTemplate> {
  return axios
    .put(
      `${window.apiBase}/i/schools/${user.activeSchoolId}/${collectionPath(type)}/${templateId}`,
      data,
      configFor(user)
    )
    .then(response => ({ ...response.data, templateType: type }));
}

export function deleteFormTemplate(
  user: AppUser,
  type: FormTemplateType,
  templateId: string
): Promise<FormTemplate> {
  return axios
    .delete(
      `${window.apiBase}/i/schools/${user.activeSchoolId}/${collectionPath(type)}/${templateId}`,
      configFor(user)
    )
    .then(response => ({ ...response.data, templateType: type }));
}
